import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import { isAdmin } from "@/utils/authentication";
import { JSX, useEffect, useState } from "react";
import { useUserData } from "@/hooks/cache";
// import { useTheme } from "@/context/ThemeProvider";
import { useTranslation } from "react-i18next";
import AIImportModal from "./AIImport/AIImportModal";
import AIImportLoader from "./AIImport/AIImportLoader";
import AIImportBackgroundService from "./AIImport/AIImportBackgroundService";
import NotificationCenter from "./NotificationCenter/NotificationCenter";
import { useAiImportStore } from "@/store/aiImports";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { AiImportStatus, User } from "@/graphql/__generated__/graphql";
import { useAIImportStatusPolling } from "@/hooks/queries";
import { ExperimentalFeatureModal } from ".";
import Cookies from "js-cookie";
import NavlinksComponent from "./Components/NavlinksComponent";
import CreateTenderButton from "./Components/CreateTenderButton";
import PortfolioComponent from "./Components/PortfolioComponent";
import { cn } from "@/lib/utils";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

const COOKIE_NAME = "hide_experimental_feature_modal_";
const helpPageUrl = import.meta.env.VITE_HELP_PAGE_URL;
const featureID = "ai_import";

/**
 * Header component that provides the main navigation and user interaction elements for the application.
 *
 * @component
 *
 * @hooks
 * - `useNavigate` - Hook to navigate programmatically.
 * - `useLocation` - Hook to access the current location object.
 * - `useUserData` - Custom hook to fetch user data.
 * - `useTranslation` - Hook for internationalization.
 * - `useAIImportStore` - Custom hook to access AI import statuses.
 * - `useAIImportStatusPolling` - Custom hook to poll AI import statuses.
 *
 * @states
 * - `isOpen` - State to manage the visibility of the mobile menu.
 * - `showAIImportModal` - State to manage the visibility of the AI import modal.
 *
 * @constants
 * - `currentLanguage` - The current language code.
 * - `showAiLoader` - Boolean indicating if the AI import loader should be displayed.
 * - `languages` - Array of supported languages with their names, codes, and icons.
 * - `links` - Array of navigation links with their href, text, and activeLink properties.
 *
 * @returns {JSX.Element} The rendered Header component.
 */
function Header(): JSX.Element {
  // Hooks
  const location = useLocation();
  const user = useUserData();
  const isAdminUser = isAdmin();
  const { t } = useTranslation(["components"]);

  const aiImportStatuses = useAiImportStore((state) => state.aiImportStatuses);
  const beginImport = useAiImportRuntime((state) => state.beginImport);
  const endImport = useAiImportRuntime((state) => state.endImport);

  // States
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showAIImportModal, setShowAIImportModal] = useState<boolean>(false);
  const [showExperimentalFeaturetModal, setShowExperimentalFeaturetModal] = useState<boolean>(false);

  // Call the useAIImportStatusPolling hook to query for data on login
  useAIImportStatusPolling();

  // If user returns (login) and there are active imports, ensure loader is visible
  // and if there is a single active import, set it as the current runtime import
  useEffect(() => {
    if (aiImportStatuses.length === 0) return;
    const activeFromServer = aiImportStatuses.filter((ai) => ai.status !== AiImportStatus.Failed && ai.status !== AiImportStatus.Success);

    // Read current store snapshot without subscribing Header to changes
    const activeImportsSnapshot = useAiImportRuntime.getState().activeImports;
    const activeIdsInStore = new Set(Object.keys(activeImportsSnapshot).map(Number));

    // Ensure all active imports from server are marked as resuming
    // Don't override imports that are explicitly marked as not resuming (new imports)
    for (const ai of activeFromServer) {
      const existingImport = activeImportsSnapshot[ai.id];
      if (!existingImport || existingImport.isResuming !== false) {
        beginImport(ai.id, true); // Mark as resuming
      }
    }

    // stop streaming for any ids no longer active
    for (const id of activeIdsInStore) {
      const stillActive = activeFromServer.some((ai) => ai.id === id);
      if (!stillActive) endImport(id);
    }
  }, [aiImportStatuses, beginImport, endImport]);

  function handleOnLinkClick() {
    setIsOpen(false);
  }

  const experimentalFeatureActionFn = () => {
    setShowAIImportModal(true);
    setShowExperimentalFeaturetModal(false);
  };

  const handleOpenExperimentalFeatureModal = () => {
    const hideModal = Cookies.get(COOKIE_NAME + featureID);
    if (hideModal === "true") {
      setShowAIImportModal(true);
      return;
    }
    setShowExperimentalFeaturetModal(true);
  };

  const links = isAdminUser
    ? []
    : [
      { href: "/dashboard", text: t("header.navlinks.dashboard"), activeLink: "/dashboard" },
      { href: "/tender/all", text: t("header.navlinks.tenders"), activeLink: "/tender" },
      { href: "/mytasks", text: t("header.navlinks.mytasks"), activeLink: "/mytasks" },
    ];


  return (
    <div className="w-full bg-white z-50 select-none -my-1">
      <div className="flex justify-between p-4">
        {/* Left side */}
        <div className="flex gap-8 items-center">
          {/* LOGO */}
          <Link to={isAdminUser ? "/admin" : "/dashboard"} data-cy="header-logo">
            <img
              src="/assets/wisebid_logo_dark.avif"
              alt="Wisebid"
              className="h-6"
            />
          </Link>

          {/* NAVLINKS */}
          <NavlinksComponent links={links} />
        </div>

        {/* Right side */}
        <div className="flex gap-4 items-center">
          {!isAdminUser && (
            <>
              {/* AI Imports Progress/History Modal */}
              <div className="hidden md:flex">
                <AIImportLoader setShowAIImportModal={setShowAIImportModal} />
              </div>

              {/* Notifications */}
              <NotificationCenter />

              {/* Create tender */}
              <CreateTenderButton handleOpenExperimentalFeatureModal={handleOpenExperimentalFeatureModal} />

              {/* Help / Feedback */}
              {(
                <a
                  href={helpPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t("help.sendfeedback")}
                  className="text-gray-500 hover:text-black transition-colors cursor-pointer"
                  aria-label={t("help.sendfeedback")}
                >
                  <QuestionMarkCircleIcon className="h-6 w-6" />
                </a>
              )}
            </>
          )}

          {/* Portfolio */}
          <PortfolioComponent user={user as User} isAdminUser={isAdminUser} />

          <div className="md:hidden flex items-center">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger data-cy="mobile-menu-trigger"><HamburgerMenuIcon className="icon-sm" /></SheetTrigger>
              <SheetContent className="w-[300px] px-0">
                <SheetHeader className="px-4 text-left">
                  <SheetTitle>WISEBID</SheetTitle>
                </SheetHeader>
                {/* AI Imports Progress/History Modal ***Mobile only*** */}
                <div className="ml-4">
                  <AIImportLoader setShowAIImportModal={setShowAIImportModal} />
                </div>
                <div className="flex flex-col mt-0">
                  {
                    links.map((link) => (
                      <Link key={link.href} to={link.href} onClick={handleOnLinkClick} data-cy={`mobile-nav-link-${link.href.replace("/", "")}`}>
                        <Button
                          variant="ghost"
                          className={cn("w-full justify-start", location.pathname.includes(link.activeLink) ? "text-black" : "text-gray-500")}
                        >
                          {link.text}
                        </Button>
                      </Link>
                    ))
                  }
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <Separator />

      {
        !isAdminUser && (
          <AIImportModal open={showAIImportModal} onOpenChange={setShowAIImportModal} />
        )
      }

      {!isAdminUser && (
        <ExperimentalFeatureModal
          featureID={featureID}
          open={showExperimentalFeaturetModal}
          onOpenChange={setShowExperimentalFeaturetModal}
          actionFn={experimentalFeatureActionFn}
        />
      )}

      {/* Background service to maintain SSE connections even when modal is closed */}
      {<AIImportBackgroundService />}
    </div>
  );
}

export default Header;
